import React from 'react'

/**
 * The SVG filter a liquid-glass surface refracts its backdrop through
 * (Chromium only). `lens` comes from useGlassLens: a displacement map the size
 * of the element whose rim pixels point outward, so the edge squeezes in what
 * lies just past it while the middle stays flat. `scale` is the furthest a rim
 * pixel reaches past the edge, in CSS px. With `dispersion` (px) each colour
 * channel is bent by a slightly different amount and the three are summed back;
 * `floor` is the surface's own tint colour, which the backdrop is flattened
 * onto first so summing translucent images does not turn the tint opaque.
 *
 * Render it inside the element that uses `backdrop-filter: url(#id)`; it lives
 * in the DOM at zero size.
 */
const LensFilter = ({ id, lens, scale = 40, dispersion = 0, floor = '#ffffff' }) => (
  <svg
    style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden', pointerEvents: 'none' }}
    width="0"
    height="0"
    aria-hidden="true"
    focusable="false"
  >
    <filter
      id={id}
      filterUnits="userSpaceOnUse"
      x="0"
      y="0"
      width={lens.width}
      height={lens.height}
      colorInterpolationFilters="sRGB"
    >
      <feImage
        href={lens.href}
        x="0"
        y="0"
        width={lens.width}
        height={lens.height}
        preserveAspectRatio="none"
        result="map"
      />
      {dispersion ? (
        <>
          <feFlood style={{ floodColor: floor }} result="floor" />
          <feComposite in="SourceGraphic" in2="floor" operator="over" result="opaque" />
          <feDisplacementMap
            in="opaque"
            in2="map"
            scale={scale - dispersion}
            xChannelSelector="R"
            yChannelSelector="G"
            result="r"
          />
          <feColorMatrix in="r" values="1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0" result="rOnly" />
          <feDisplacementMap in="opaque" in2="map" scale={scale} xChannelSelector="R" yChannelSelector="G" result="g" />
          <feColorMatrix in="g" values="0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0" result="gOnly" />
          <feDisplacementMap
            in="opaque"
            in2="map"
            scale={scale + dispersion}
            xChannelSelector="R"
            yChannelSelector="G"
            result="b"
          />
          <feColorMatrix in="b" values="0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0" result="bOnly" />
          <feComposite in="rOnly" in2="gOnly" operator="arithmetic" k2="1" k3="1" result="rg" />
          <feComposite in="rg" in2="bOnly" operator="arithmetic" k2="1" k3="1" result="dispersed" />
          <feDisplacementMap
            in="SourceGraphic"
            in2="map"
            scale={scale}
            xChannelSelector="R"
            yChannelSelector="G"
            result="shape"
          />
          <feComposite in="dispersed" in2="shape" operator="in" />
        </>
      ) : (
        <feDisplacementMap in="SourceGraphic" in2="map" scale={scale} xChannelSelector="R" yChannelSelector="G" />
      )}
    </filter>
  </svg>
)

export default LensFilter
